export default class RecordService {
  _refIds = [];
  _prefill = {};
  _records = {};
  _updatedRecords = {};
  _originalRecords = {};
  _restrictedFields = {};
  _loadRecordsPromise = null;
  _passkeys = null;
  _invalidPasskeys = null;
  _numberOfUpdates = 0;
  _recordReferences = [];
  _reportRids = {};
  _signedLinkTargets = null;
  _resolvedSignedRids = null;
  _signedLinkRecordNotFoundWarn = false;
  _signedLinkResolve = null;
  _associatedLink = null;
  _editableServerSignedSession = false;
  _editableServerSignedAccess = null;
  constructor(ActionService, AuthService, FEService, QueryParamService, TemplateService, EventService) {
    'ngInject';
    this.as = ActionService;
    this.fe = FEService;
    this.auth = AuthService;
    this.ts = TemplateService;
    this.es = EventService;
    this.qps = QueryParamService;
    this.subscribeEvents();
  }
  
  // Merged rid map — URL rids, PSA-resolved rids, and report row rids.
  get rids() {
    return {
      ...this.qps.getParsedParam('rids'),
      ...(this._resolvedSignedRids || {}),
      ...this.reportRids
    };
  }
  // Report row rids — omitted on signed one-shot print sessions.
  get reportRids() {
    if (this.ts.template?.signTemplate === true) {
      return this.isSignedPrintSession() ? {} : this._reportRids;
    }
    return this.as.isUpdate ? this._reportRids : {};
  }
  // Per-target actions from loadFormRecords after PSA resolve (null while passkey-gated).
  get signedLinkTargets() { return this._signedLinkTargets }
  // True when warn-configured targets had a rid but no record loaded (set during load).
  get signedLinkRecordNotFoundWarn() { return !!this._signedLinkRecordNotFoundWarn }
  // Load-time sealed reconcile snapshot for the next signed save.
  get signedLinkResolve() { return this._signedLinkResolve }
  // Link metadata from loadFormRecords (host table, passkey config, redirect, etc.).
  get associatedLink() { return this._associatedLink }

  // True when link passkey config has entries still missing from the request.
  linkPasskeyRequiredButMissing(associatedLink, passkeys) {
    const entries = associatedLink?.passkey?.entries;
    if (!entries?.length) return false;
    return entries.some((entry) => {
      const v = passkeys?.[entry.referenceId];
      if (v == null) return true;
      return typeof v === 'string' && v.trim() === '';
    });
  }

  // Blocks signed preload until required link passkeys are collected.
  signedLinkPasskeyGateActive(associatedLink) {
    return this.linkPasskeyRequiredButMissing(associatedLink, this._passkeys);
  }

  // Reset invalid passkey state before the user retries the modal.
  clearInvalidPasskeys() {
    this._invalidPasskeys = null;
  }

  // Clear stored passkeys so the modal can re-prompt after a wrong entry.
  clearPasskeys() {
    this._passkeys = null;
  }

  // Re-run loadFormRecords after the user submits passkeys from the modal.
  async reloadRecordsWithPasskeys() {
    this._invalidPasskeys = null;
    this._loadRecordsPromise = this.loadRecordData();
    return this._loadRecordsPromise;
  }

  get recordsPromise() { return this._loadRecordsPromise } // async, safer than `get records`
  get records() { return this._records }
  get updatedRecords() { return this._updatedRecords }
  get hasUpdates() { return !!this._numberOfUpdates }
  get numUpdates() { return this._numberOfUpdates }
  get recordReferences() { return this._recordReferences }
  
  // Link target action (print/update/submit) for a reference from PSA resolve.
  signedTargetAction(refId) {
    const target = this._signedLinkTargets?.find((t) => t.referenceId === refId);
    return target?.action;
  }

  // Full signed link target config for a reference (not found / on-empty policies).
  signedTargetConfig(refId) {
    return this._signedLinkTargets?.find((t) => t.referenceId === refId);
  }

  // Submit-target policy when the user leaves the ref empty (ignore vs create).
  onEmptyRecordActionForRef(refId) {
    const target = this.signedTargetConfig(refId);
    return target?.onEmptyRecordAction === 'ignore' ? 'ignore' : 'create';
  }

  // Skip required-field validation for submit targets with onEmptyRecordAction ignore.
  shouldEnforceRequiredFields(refId) {
    if (this.ts.template?.signTemplate !== true) return true;
    if (this.signedTargetAction(refId) !== 'submit') return true;
    if (this.onEmptyRecordActionForRef(refId) !== 'ignore') return true;
    return this.hasMeaningfulRecordPayload(this.updatedRecords?.[refId]);
  }

  // True when updated record has at least one non-empty field value.
  hasMeaningfulRecordPayload(record) {
    if (!record || typeof record !== 'object') return false;
    const keys = Object.keys(record);
    if (!keys.length) return false;
    return keys.some((fid) => {
      const cell = record[fid];
      if (cell == null) return false;
      const v = cell.value;
      if (v === null || v === undefined) return false;
      return typeof v !== 'string' ? true : v.trim() !== '';
    });
  }

  // Copy loaded rid into submit upsert when custom key field is still empty.
  mergeRecordPrimaryKeyForSubmit(refId, rec) {
    const ref = this.ts.references?.find((r) => r.id === refId);
    const fid = ref?.tableSchema?.keyFieldId;
    if (!fid || fid === '3') return;
    const existing = rec?.[fid]?.value;
    if (existing !== null && existing !== undefined && String(existing).trim() !== '') return;
    const fromRecord = this._records?.[refId]?.[fid];
    if (fromRecord?.value === null || fromRecord?.value === undefined) return;
    if (typeof fromRecord.value === 'string' && fromRecord.value.trim() === '') return;
    rec[fid] = angular.copy(fromRecord);
  }

  // Attach report-row rid to temp ref upsert payload for signed submit/update.
  mergeReportRowPrimaryKey(refId, rec) {
    const ref = this.ts.references?.find((r) => r.id === refId);
    if (!ref?._temp) return;
    const fid = ref?.tableSchema?.keyFieldId || '3';
    const existing = rec?.[fid]?.value;
    if (existing !== null && existing !== undefined && String(existing).trim() !== '') return;
    const fromRid = this._reportRids[refId];
    if (fromRid !== null && fromRid !== undefined && String(fromRid).trim() !== '') {
      rec[fid] = { value: fromRid, display: null };
      return;
    }
    const fromRecord = this._records?.[refId]?.[fid];
    if (fromRecord?.value === null || fromRecord?.value === undefined) return;
    if (typeof fromRecord.value === 'string' && fromRecord.value.trim() === '') return;
    rec[fid] = angular.copy(fromRecord);
  }

  // Apply report primary keys across the upsert records map before signed upsert.
  withSignedTempReportRecordKeys(records) {
    if (!records || typeof records !== 'object') return records;
    return Object.keys(records).reduce((acc, refId) => {
      const rec = { ...(records[refId] || {}) };
      this.mergeReportRowPrimaryKey(refId, rec);
      acc[refId] = rec;
      return acc;
    }, {});
  }

  // Build signed upsert payload honoring per-target submit and eligibility rules.
  filterSignedSubmitUpsertRecords(records) {
    if (!records || typeof records !== 'object') return {};
    return Object.keys(records).reduce((acc, refId) => {
      const ref = this.ts.references?.find((r) => r.id === refId);
      if (!ref || !this.referenceEligibleForUpsert(ref)) return acc;

      const rec = records[refId];
      if (this.signedTargetAction(refId) === 'submit' && this.onEmptyRecordActionForRef(refId) === 'ignore') {
        if (!this.hasMeaningfulRecordPayload(rec)) return acc;
      }
      acc[refId] = rec ?? {};
      if (this.signedTargetAction(refId) === 'submit') {
        this.mergeRecordPrimaryKeyForSubmit(refId, acc[refId]);
      }
      return acc;
    }, {});
  }

  // True when ref has a non-empty rid from URL, PSA resolve, or report.
  hasUsableRidForRef(refId) {
    const v = this.rids[refId];
    if (v === null || v === undefined) return false;
    return typeof v !== 'string' ? true : v.trim() !== '';
  }

  // Ref ids to keep after PSA resolve — submit always; update/print need a rid.
  workableSignedLinkRefIds(targets, resolvedRids) {
    return (targets || []).filter((t) => {
      if (t.action === 'submit') return true;
      if (t.action === 'update' || t.action === 'print') {
        const v = resolvedRids?.[t.referenceId];
        if (v === null || v === undefined) return false;
        return typeof v !== 'string' ? true : v.trim() !== '';
      }
      return false;
    }).map((t) => t.referenceId);
  }

  // True when warn-configured update/print targets failed to load any record data.
  signedLinkUpdatePrintTargetsMissingRecords() {
    const targets = this._signedLinkTargets || [];
    const needsLoad = targets.filter((t) => (t.action === 'update' || t.action === 'print') && t.recordNotFoundAction === 'warn');
    if (!needsLoad.length) return false;
    return needsLoad.some((t) => {
      const rec = this._records?.[t.referenceId];
      if (!rec) return true;
      return !Object.keys(rec).some((fid) => {
        const v = rec[fid]?.value;
        if (v === null || v === undefined) return false;
        return typeof v !== 'string' ? true : v.trim() !== '';
      });
    });
  }

  // serverSignedAccess one-shot print/download session (not save-and-keep-working).
  isSignedPrintSession() {
    if (!this.effectiveServerSignedAccessRaw()?.length) return false;
    return !this._editableServerSignedSession;
  }

  // Session token from keep-working wins over URL serverSignedAccess param.
  effectiveServerSignedAccessRaw() {
    if (this._editableServerSignedAccess?.length) return this._editableServerSignedAccess;
    return this.qps.getRawParam('serverSignedAccess');
  }

  // Omit submitLinkId once editable server-signed session has started.
  effectiveSubmitLinkIdRaw() {
    if (this._editableServerSignedSession) return null;
    return this.qps.getRawParam('submitLinkId');
  }

  // Omit PSA token once editable server-signed session has started.
  effectiveProviderSignedAccessRaw() {
    if (this._editableServerSignedSession) return null;
    return this.qps.getRawParam('providerSignedAccess');
  }

  // User chose save-and-keep-working on a signed link (editable session).
  isEditableSignedWorkingSession() {
    return !!this._editableServerSignedSession;
  }

  // Switch to editable session using serverSignedAccess returned from upsert.
  applyEditableSignedWorkingSession(serverSignedAccess) {
    if (!serverSignedAccess?.length) return;
    this._editableServerSignedAccess = serverSignedAccess;
    this._editableServerSignedSession = true;
    this._signedLinkResolve = null;
  }

  // Signed link with no targets — print/download metadata only, no record load.
  isDownloadOnlySignedLink() {
    if (this.ts.template?.signTemplate !== true) return false;
    if (this.isSignedPrintSession()) return true;
    const signed = this.effectiveProviderSignedAccessRaw()?.length > 0;
    const submitLink = this.effectiveSubmitLinkIdRaw()?.length > 0;
    if (!signed && !submitLink) return false;
    const targets = this._signedLinkTargets ?? this._associatedLink?.targets;
    return Array.isArray(targets) && targets.length === 0;
  }

  // True for print-only, download-only, or one-shot serverSigned print sessions.
  isSignedPrintOnlyLinkSession() {
    if (this.isSignedPrintSession()) return true;
    if (this.isDownloadOnlySignedLink()) return true;
    if (this.ts.template?.signTemplate !== true) return false;
    if (this._editableServerSignedSession) return false;
    const signed = this.effectiveProviderSignedAccessRaw()?.length > 0;
    const submitLink = this.effectiveSubmitLinkIdRaw()?.length > 0;
    if (!signed && !submitLink) return false;
    // Mirror render.component.js signedEffectiveAction (all-print targets)
    const targets = this._signedLinkTargets ?? this._associatedLink?.targets ?? [];
    if (!targets.length) return true;
    const hasSubmit = targets.some((t) => t.action === 'submit');
    const hasUpdate = targets.some((t) => t.action === 'update');
    const hasPrint = targets.some((t) => t.action === 'print');
    return hasPrint && !hasSubmit && !hasUpdate;
  }

  // Parent ref for a temp report child — inherits signed target action from parent.
  parentRefIdForTemp(refId) {
    const ref = this.ts.references?.find((r) => r.id === refId);
    if (!ref?._temp) return null;
    const rel = (this.ts.relations || []).find((r) => r.childId === refId);
    return rel?.parentId ?? null;
  }

  // Target action for ref or its temp parent (report rows under a signed target).
  signedLinkActionForRef(refId) {
    const direct = this.signedTargetAction(refId);
    if (direct) return direct;
    const parentId = this.parentRefIdForTemp(refId);
    return parentId ? this.signedTargetAction(parentId) : undefined;
  }

  // Per-ref print/update/submit mode for signed templates (mixed links differ per table).
  effectiveRefMode(refId) {
    if (this.ts.template?.signTemplate !== true) return null;
    if (this.isSignedPrintSession()) return 'print';
    if (this.isDownloadOnlySignedLink()) return 'print';
    const linkAction = this.signedLinkActionForRef(refId);
    if (linkAction === 'print') return 'print';
    if (linkAction === 'update') return 'update';
    if (linkAction === 'submit') {
      return this.hasUsableRidForRef(refId) ? 'update' : 'submit';
    }
    return 'print';
  }

  // Per-ref print mode — delegates to effectiveRefMode.
  isRefPrint(refId) {
    return this.effectiveRefMode(refId) === 'print';
  }

  // Per-ref submit mode — new insert when target has no rid yet.
  isRefSubmit(refId) {
    return this.effectiveRefMode(refId) === 'submit';
  }

  // Per-ref update mode — existing record loaded by signed link rid.
  isRefUpdate(refId) {
    return this.effectiveRefMode(refId) === 'update';
  }

  // False for print-only targets on mixed signed links.
  isRefEditable(refId) {
    return !this.isRefPrint(refId);
  }

  // True when any non-temp ref is in submit mode on a signed editable session.
  hasAnyRefInSubmitMode() {
    if (this.isSignedPrintSession()) return false;
    const refs = this.ts.references?.filter((r) => !r._temp) || [];
    return refs.some((r) => this.isRefSubmit(r.id));
  }

  // Whether a ref may be included in upsert — signed targets override allowSubmitOnUpdate.
  referenceEligibleForUpsert(ref) {
    // Signed templates: insert/update per signedLinkTargets only (allowSubmitOnUpdate is ignored).
    if (this.ts.template?.signTemplate === true) {
      const serverRaw = this.effectiveServerSignedAccessRaw();
      if (serverRaw?.length && !this._editableServerSignedSession) return false;
      const action = this.signedLinkActionForRef(ref.id);
      if (action === 'print') return false;
      if (action === 'update') return true;
      if (action === 'submit') {
        if (this.hasUsableRidForRef(ref.id)) return true;
        if (this.onEmptyRecordActionForRef(ref.id) === 'ignore') {
          return this.hasMeaningfulRecordPayload(this.updatedRecords?.[ref.id]);
        }
        return true;
      }
      return false;
    }
    if (ref.allowSubmitOnUpdate) return true;
    if (this.rids[ref.id] !== undefined) return true;
    const serverRaw = this.qps.getRawParam('serverSignedAccess');
    if (serverRaw?.length) return true;
    const signedRaw = this.qps.getRawParam('providerSignedAccess');
    if (!signedRaw?.length) return false;
    const fid = ref?.tableSchema?.keyFieldId || '3';
    const v = this._records[ref.id]?.[fid]?.value;
    return v !== undefined && v !== null && v !== '';
  }

  // Dirty records to upsert — filters by signed target action and submit/on-empty rules.
  get updatedRecordsWithRefs() {
    const isSignedTemplate = this.ts.template?.signTemplate === true;
    const serverSigned = this.effectiveServerSignedAccessRaw()?.length > 0;
    const treatAsSubmit = isSignedTemplate
      ? !this._editableServerSignedSession
        && !serverSigned
        && !this.isDownloadOnlySignedLink()
        && (this._signedLinkTargets?.some((t) => t.action === 'submit') ?? false)
      : this.as.isSubmit;
    if (treatAsSubmit) {
      if (!isSignedTemplate) return this.updatedRecords;
      return this.filterSignedSubmitUpsertRecords(this.updatedRecords);
    }
    if (isSignedTemplate && this._editableServerSignedSession && !this.isDownloadOnlySignedLink()) {
      const submitRecords = Object.keys(this.updatedRecords || {}).reduce((acc, refId) => {
        if (this.isRefSubmit(refId)) acc[refId] = this.updatedRecords[refId];
        return acc;
      }, {});
      const submitPayload = this.filterSignedSubmitUpsertRecords(submitRecords);
      const updateRefs = this.ts.references?.filter(
        (r) => this.referenceEligibleForUpsert(r) && this.isRefUpdate(r.id)
      );
      const updatePayload = updateRefs?.reduce((acc, ref) => {
        const rec = this.updatedRecords[ref.id];
        if (rec && Object.keys(rec).length > 0) acc[ref.id] = rec;
        return acc;
      }, {}) || {};
      return { ...updatePayload, ...submitPayload };
    }
    const refs = this.ts.references?.filter(r => this.referenceEligibleForUpsert(r));
    if (!isSignedTemplate) {
      return refs?.reduce((acc, ref) => {
        acc[ref.id] = this.updatedRecords[ref.id] || {};
        return acc;
      }, {}) || {};
    }
    return refs?.reduce((acc, ref) => {
      const rec = this.updatedRecords[ref.id];
      if (rec && Object.keys(rec).length > 0) acc[ref.id] = rec;
      return acc;
    }, {}) || {};
  }
  get recordCount() {
    return this._recordCount || 0;
  }
  
  set updatedRecords(updRecs) { 
    this._updatedRecords = updRecs;
    this.setNumUpdates(updRecs);
  }

  set passkeys(passkeysObj) { 
    this._passkeys = passkeysObj;
  }
  
  get passkeys() {
    return this._passkeys;
  }
  
  get invalidPasskeys() {
    return this._invalidPasskeys;
  }
  
  //#region Initialize

  // Reset signed-link session state and start loadFormRecords (or restore keep-working from URL).
  preLoadRecords() {
    this._reportRids = {};
    this._originalRecords = {};
    this._invalidPasskeys = null;
    this._signedLinkTargets = null;
    this._resolvedSignedRids = null;
    this._signedLinkRecordNotFoundWarn = false;
    this._signedLinkResolve = null;
    this._associatedLink = null;
    this._editableServerSignedAccess = null;
    this._editableServerSignedSession = false;
    const keepWorkingParam = this.qps.getRawParam('keepWorking');
    const serverTok = this.qps.getRawParam('serverSignedAccess');
    if (keepWorkingParam && serverTok?.length) {
      this._editableServerSignedAccess = serverTok;
      this._editableServerSignedSession = true;
    }
    this._loadRecordsPromise = this.loadRecordData();
    return this._loadRecordsPromise;
  }
  
  initObjKeys(keyArray, v0) { // {'key1': v0, 'key2': v0, ...}
    return keyArray.reduce((acc, key) => ( {...acc, [key]: angular.copy(v0)} ), {})
  }
  
  structureRecords(tokens, refIds) {
    this._records =          this.initObjKeys(refIds, {});
    this.updatedRecords =    this.initObjKeys(refIds, {});
    this._restrictedFields = this.initObjKeys(refIds, []);
    tokens.forEach(t => this._records[t.referenceId][t.fieldId] = {});
  }

  // Trim refs, records, and prefills to signed-link allowed ref ids only.
  pruneToReferenceIds(allowedRefIds) {
    const allowed = new Set(allowedRefIds || []);
    const refs = (this.ts.references || []).filter((r) => !r._temp && allowed.has(r.id));
    this._recordReferences = refs;
    this._refIds = refs.map((r) => r.id);

    const nextRecords = {};
    const nextUpdated = {};
    const nextRestricted = {};
    this._refIds.forEach((refId) => {
      nextRecords[refId] = this._records?.[refId] || {};
      nextUpdated[refId] = this.updatedRecords?.[refId] || {};
      nextRestricted[refId] = this._restrictedFields?.[refId] || [];
    });
    this._records = nextRecords;
    this.updatedRecords = nextUpdated;
    this._restrictedFields = nextRestricted;
    if (this._prefill && typeof this._prefill === 'object') {
      this._prefill = Object.keys(this._prefill).reduce((acc, refId) => {
        if (allowed.has(refId)) acc[refId] = this._prefill[refId];
        return acc;
      }, {});
    }
  }
  
  isValidRid(rid) {
    return rid && rid !== '0';
  }

  // Rids read from loaded record key fields (refs with no loaded value are omitted).
  resolveRidsFromLoadedRecords(references) {
    const out = {};
    (references || []).filter((r) => !r._temp).forEach((ref) => {
      const rec = this._records[ref.id];
      if (!rec) return;
      const fid = ref?.tableSchema?.keyFieldId || '3';
      const v = rec[fid]?.value;
      if (v === null || v === undefined) return;
      if (typeof v === 'string' && v.trim() === '') return;
      out[ref.id] = v;
    });
    return out;
  }

  // loadFormRecords entry — signed links omit URL rids; server resolves targets and associatedLink.
  async loadRecordData() {
    const templateData = await this.ts.getTemplateData();
    if (!templateData) return;
    const {template: {tokens, references}} = templateData;
    this._recordCount = 0;
    const signedRaw = this.effectiveProviderSignedAccessRaw();
    const signed = signedRaw?.length > 0;
    const serverSignedRaw = this.effectiveServerSignedAccessRaw();
    const serverSigned = serverSignedRaw?.length > 0;
    const submitLinkRaw = this.effectiveSubmitLinkIdRaw();
    const submitLink = submitLinkRaw?.length > 0;
    const isSignedTemplate = this.ts.template?.signTemplate === true;
    const resolveSignedLink = isSignedTemplate && (signed || submitLink) && !this._editableServerSignedSession;

    // Signed: all link targets; unsigned update: rids or allowSubmitOnUpdate only.
    this._recordReferences = resolveSignedLink || signed || submitLink || serverSigned
      ? this.ts.references
      : this.as.isSubmit
        ? this.ts.references
        : this.ts.references?.filter(r => this.isValidRid(this.rids[r.id]) || r.allowSubmitOnUpdate);
    this._refIds = references.filter(ref => !ref._temp).map(ref => ref.id);
    this.structureRecords(tokens.filter(t => !t._temp), this._refIds);

    const refsToLoad = (references || []).filter((ref) => !ref._temp);
    if (refsToLoad.length > 0 && (this.as.shouldHaveRecords || resolveSignedLink || serverSigned)) {
      const templateId = this.ts.templateId;
      const rids = this.rids;
      const passkeys = this._passkeys && Object.keys(this._passkeys).length > 0 ? this._passkeys : undefined;
      const loadReq = { type: 'loadFormRecords', templateId, ...(passkeys ? { passkeys } : {}) };
      if (serverSigned) loadReq.serverSignedAccess = serverSignedRaw;
      if (signed) loadReq.providerSignedAccess = signedRaw;
      if (submitLink) loadReq.submitLinkId = submitLinkRaw;
      const omitLoadRids = isSignedTemplate || signed || submitLink || serverSigned;
      // Signed sessions resolve rids on server from PSA host row, submit link, or serverSignedAccess.
      if (!omitLoadRids && Object.keys(rids || {}).length) loadReq.rids = rids;
      const { "data": { results, invalidPasskeys, signedLinkTargets, resolvedRids, associatedLink, resolvedPrefills, recordNotFoundWarn, signedLinkResolve } } = await this.queryRecords(loadReq);
      this._invalidPasskeys = invalidPasskeys && Object.keys(invalidPasskeys).length > 0 ? invalidPasskeys : null;
      this._associatedLink = associatedLink ?? null;
      const passkeyGate = this.signedLinkPasskeyGateActive(associatedLink)
        || (this._invalidPasskeys && Object.keys(this._invalidPasskeys).length > 0);
      if (!passkeyGate && typeof signedLinkResolve === 'string' && signedLinkResolve.length) {
        this._signedLinkResolve = signedLinkResolve;
      } else if (passkeyGate) {
        this._signedLinkResolve = null;
      }
      if (resolveSignedLink) {
        this._signedLinkTargets = passkeyGate
          ? null
          : (Array.isArray(signedLinkTargets) ? signedLinkTargets : null);
        this._resolvedSignedRids = passkeyGate
          ? null
          : (resolvedRids && Object.keys(resolvedRids).length ? resolvedRids : null);
        if (!passkeyGate) {
          // Server flag only here — client missing-record check runs after results fill _records.
          this._signedLinkRecordNotFoundWarn = recordNotFoundWarn === true;
          if (this._signedLinkTargets?.length) {
            const allowedRefIds = this.workableSignedLinkRefIds(
              this._signedLinkTargets,
              this._resolvedSignedRids
            );
            this.pruneToReferenceIds(allowedRefIds);
          }
        }
      }
      if (!this._invalidPasskeys) {
        results.forEach((result, _i) => {
          const refId = result.referenceId;
          if (refId && result.records && result.records[0]) {
            this._recordCount++;
            this._records[refId] = result.records[0];
          }
        });
        if (resolveSignedLink && !passkeyGate) {
          this._signedLinkRecordNotFoundWarn = this._signedLinkRecordNotFoundWarn
            || this.signedLinkUpdatePrintTargetsMissingRecords();
        }
        if (serverSigned && (!resolveSignedLink || this._editableServerSignedSession)) {
          const fromRecords = this.resolveRidsFromLoadedRecords(references);
          if (Object.keys(fromRecords).length) {
            this._resolvedSignedRids = { ...(this._resolvedSignedRids || {}), ...fromRecords };
          }
          if (!passkeyGate && resolvedRids && Object.keys(resolvedRids).length) {
            this._resolvedSignedRids = { ...(this._resolvedSignedRids || {}), ...resolvedRids };
          }
          if (!passkeyGate && Array.isArray(signedLinkTargets) && signedLinkTargets.length) {
            this._signedLinkTargets = signedLinkTargets;
          } else if (!passkeyGate && associatedLink?.targets?.length) {
            this._signedLinkTargets = associatedLink.targets;
          }
          if (!passkeyGate) {
            this._signedLinkRecordNotFoundWarn = this._signedLinkRecordNotFoundWarn
              || recordNotFoundWarn === true
              || this.signedLinkUpdatePrintTargetsMissingRecords();
          }
        }
        if (serverSigned && !resolveSignedLink) {
          if (this._editableServerSignedSession && this._signedLinkTargets?.length) {
            if (!passkeyGate) {
              this._signedLinkRecordNotFoundWarn = this._signedLinkRecordNotFoundWarn
                || recordNotFoundWarn === true
                || this.signedLinkUpdatePrintTargetsMissingRecords();
            }
            const allowedRefIds = this.workableSignedLinkRefIds(
              this._signedLinkTargets,
              this._resolvedSignedRids
            );
            this.pruneToReferenceIds(allowedRefIds);
          } else {
            const loadedRefIds = (results || [])
              .map((r) => r?.referenceId)
              .filter((id) => typeof id === 'string' && id.length > 0);
            if (loadedRefIds.length) this.pruneToReferenceIds(loadedRefIds);
          }
        }
      }
      if (resolvedPrefills && !passkeyGate) {
        this.applyResolvedPrefills(resolvedPrefills, this._refIds);
      }
    }

    this._originalRecords = angular.copy(this._records);
    if (this.ts.template?.signTemplate === true) {
      this.es.dispatchEvent(this.es.actions.RECORDS_UPDATED, this._records);
    }
    return this._records;
  }
  
  //#endregion Initialize
  
  //#region RecUpdates
  
  fieldUpdated(token, model) {
    const { fieldSchema, fieldId, 'referenceId': refId } = token;
    if (!fieldSchema) return; // not-found

    const updRecs = {...this._updatedRecords};
    
    this.shouldUpdate(token, model)
      ? updRecs[refId] = {...updRecs[refId], [fieldId]: model}
      : updRecs[refId] && delete updRecs[refId][fieldId];
      
    this.updatedRecords = updRecs;
    
    this.es.dispatchEvent(this.es.actions.RECORDS_UPDATED, this.records);
  }
  
  valIfDefined(val) {
    return [null, undefined, '', {}].some(v => angular.equals(v, val)) ? undefined : val;
  }

  shouldUpdate(token, model) {
    const { 'referenceId': refId, fieldId, 'fieldSchema': { isReadOnly } } = token;
    const newVal = this.valIfDefined(model?.value);
    const origVal = this.valIfDefined(this._originalRecords[refId]?.[fieldId]?.value);
    return !isReadOnly 
      && !this._restrictedFields[refId]?.includes(fieldId)
      && !angular.equals(origVal, newVal);
  }

  restrictField(refId, fid) { // can happen after failed upsert
    this._records[refId][fid] = this._originalRecords[refId][fid];
    this._restrictedFields[refId].push(fid);
    
    delete this._prefill[refId]?.[fid]; // prevent prefill field use without permissions
  }
  
  resetRecords() { 
    this._records = angular.copy(this._originalRecords);
    this.updatedRecords = this.initObjKeys(this._refIds, {});
  }
  
  //#endregion RecUpdates
  
  // Attach signed session tokens from URL/session state when caller omitted them.
  queryRecords(params = {}) {
    const connection = this.auth.suppliedConnection();
    const request = { connection, ...params };
    if (!request.serverSignedAccess) {
      const serverSignedRaw = this.effectiveServerSignedAccessRaw();
      if (serverSignedRaw?.length) request.serverSignedAccess = serverSignedRaw;
    }
    if (!request.providerSignedAccess) {
      const signedRaw = this.effectiveProviderSignedAccessRaw();
      if (signedRaw?.length) request.providerSignedAccess = signedRaw;
    }
    if (!request.submitLinkId) {
      const submitLinkRaw = this.effectiveSubmitLinkIdRaw();
      if (submitLinkRaw?.length) request.submitLinkId = submitLinkRaw;
    }
    Object.keys(request).forEach(key => request[key] == null && delete request[key]);
    return this.fe.httpsCallable('queryRecords', request);
  }
  
  // True when upsert request includes at least one non-empty rid.
  hasMeaningfulRids(rids) {
    if (!rids || typeof rids !== 'object') return false;
    return Object.keys(rids).some((refId) => {
      const v = rids[refId];
      if (v === null || v === undefined) return false;
      return typeof v !== 'string' ? true : v.trim().length > 0;
    });
  }

  // Write resolved rids into loaded record key fields after save-and-keep-working.
  syncRecordKeyFieldsFromRids(rids, references) {
    if (!rids || typeof rids !== 'object') return;
    (references || this.ts.references || []).forEach((ref) => {
      if (ref._temp) return;
      const refId = ref.id;
      const v = rids[refId];
      if (v === null || v === undefined) return;
      if (typeof v === 'string' && v.trim() === '') return;
      if (!this._records[refId]) return;
      const fid = ref?.tableSchema?.keyFieldId || '3';
      if (!this._records[refId][fid]) this._records[refId][fid] = {};
      this._records[refId][fid].value = v;
    });
  }

  // After signed save-and-keep-working — merge rids, flip submit targets to update, reset dirty state.
  applySaveAndKeepWorkingState(responseRids) {
    if (responseRids && typeof responseRids === 'object') {
      this._resolvedSignedRids = { ...(this._resolvedSignedRids || {}), ...responseRids };
      this.syncRecordKeyFieldsFromRids(responseRids);
    }
    if (this._signedLinkTargets?.length && responseRids && typeof responseRids === 'object') {
      this._signedLinkTargets = this._signedLinkTargets.map((t) => {
        if (t.action !== 'submit') return t;
        const refId = t.referenceId;
        const rid = responseRids[refId];
        if (rid === null || rid === undefined) return t;
        if (typeof rid === 'string' && rid.trim() === '') return t;
        // Skip-if-empty submit with no usable rid stays submit (same as pure submit + keep working).
        if (t.onEmptyRecordAction === 'ignore' && !this.hasUsableRidForRef(refId)) return t;
        return { ...t, action: 'update' };
      });
      if (this._associatedLink) {
        this._associatedLink = { ...this._associatedLink, targets: this._signedLinkTargets };
      }
    }
    this._originalRecords = angular.copy(this._records);
    this.updatedRecords = this.initObjKeys(this._refIds, []);
    this.es.dispatchEvent(this.es.actions.RECORDS_UPDATED, this._records);
  }

  // Signed templates always use updateRecords; unsigned picks add vs update from rids/session.
  async upsertRecords(updatedRecords=this.updatedRecordsWithRefs, rids=this.rids, options={}) {
    const nonFormPrefill = this.nonFormPrefills();
    const recs = angular.merge({}, nonFormPrefill, updatedRecords); 
    const request = this.buildUpsertRecordsQuery(recs, rids, options);
    const isSignedTemplate = this.ts.template?.signTemplate === true;
    const funcName = isSignedTemplate ? 'updateRecords' : (
      this.as.isSubmit
        ? (this.hasMeaningfulRids(request.rids) || request.serverSignedAccess ? 'updateRecords' : 'addRecords')
        : (this.hasMeaningfulRids(request.rids) || request.providerSignedAccess || request.submitLinkId || request.serverSignedAccess
          ? 'updateRecords'
          : 'addRecords')
    );
    const response = await this.fe.httpsCallable(funcName, request);
    return response;
  }
  //#region Queries
  
  queryEmbeddedHtml(query){
    const connection = this.auth.suppliedConnection();
    const request = { query, connection, templateId: this.ts.template.id };
    return this.fe.httpsCallable('queryReport', request);
  }
  
  // Upsert payload — signed links send tokens instead of rids; passkeys on PSA/submit only.
  buildUpsertRecordsQuery(records, rids, options={}) {
    const signedRaw = this.effectiveProviderSignedAccessRaw();
    const signed = signedRaw?.length > 0;
    const serverSignedRaw = this.effectiveServerSignedAccessRaw();
    const serverSigned = serverSignedRaw?.length > 0;
    const submitLinkRaw = serverSigned ? null : this.effectiveSubmitLinkIdRaw();
    const submitLink = submitLinkRaw?.length > 0;
    const linkNeedsPasskeys = this.ts.template?.signTemplate === true
      && (signed || submitLink)
      && !this._editableServerSignedSession;
    const passkeys = linkNeedsPasskeys && this._passkeys && Object.keys(this._passkeys).length > 0
      ? this._passkeys
      : undefined;
    const payloadRecords = this.ts.template?.signTemplate === true
      ? this.withSignedTempReportRecordKeys(records)
      : records;
    const upsertQuery = {
      records: payloadRecords,
      template: this.ts.toRaw({includeRefs: true, includeRels: true}),
      connection: this.auth.suppliedConnection(),
      ...(signed ? { providerSignedAccess: signedRaw } : {}),
      ...(submitLink ? { submitLinkId: submitLinkRaw } : {}),
      ...(serverSigned ? { serverSignedAccess: serverSignedRaw } : {}),
      ...(!serverSigned && this._signedLinkResolve ? { signedLinkResolve: this._signedLinkResolve } : {}),
      ...(passkeys ? { passkeys } : {}),
      ...(options.keepWorking ? { keepWorking: true } : {}),
      ...(!signed && !submitLink && !serverSigned && Object.keys(rids || {}).length ? { rids } : {})
    };
    console.log(`UPSERTING ${this._numberOfUpdates} changes`);
    return upsertQuery;
  }

  addReportRid(refId, rid, rec) {
    this._reportRids[refId] = rid;
    this._originalRecords[refId] = angular.copy(rec);
  }

  removeReportRid(refId) {
    delete this._reportRids[refId];
    delete this._originalRecords[refId];
    delete this._updatedRecords[refId];
    this._recordReferences = this._recordReferences.filter(r => r.id != refId);
  }
  
  //#endregion Queries

  //#region Subscriptions
  
  subscribeEvents() {
    this.es.subscribeEvent(
      this.es.actions.FIELD_UPDATED, 
      ({token, model}) => this.fieldUpdated(token, model),
      'FIELD_UPDATED_ANY'
    );
  }
  
  setNumUpdates(updatedRecs) {
    this._numberOfUpdates = Object.values(updatedRecs).reduce((acc, refModel) => {
      return acc + Object.keys(refModel).length;
    }, 0);
  }
  
  //#endregion Subscriptions
  
  //#region Prefill
  
  getPrefill(refId, fieldId) {
    return this._prefill[refId]?.[fieldId];
  }

  // Signed links resolve prefills from host row on server — ignore URL prefill param.
  usesServerLinkPrefills() {
    return this.ts.template?.signTemplate === true;
  }

  // Apply server-resolved prefills from PSA host row (replaces URL prefill on signed links).
  applyResolvedPrefills(resolvedPrefills, allowedRefIds = null) {
    if (!resolvedPrefills || typeof resolvedPrefills !== 'object') return;
    const allowed = allowedRefIds != null
      ? new Set((allowedRefIds || []).map((id) => String(id)))
      : null;
    const pf = {};
    Object.entries(resolvedPrefills).forEach(([refId, fields]) => {
      if (allowed && !allowed.has(String(refId))) return;
      if (!fields || typeof fields !== 'object') return;
      pf[refId] = {};
      Object.entries(fields).forEach(([fid, cell]) => {
        const v = cell?.value !== undefined && cell?.value !== null ? cell.value : cell?.display;
        if (v !== undefined && v !== null) pf[refId][fid] = String(v);
      });
      if (!Object.keys(pf[refId]).length) delete pf[refId];
    });
    const pfIds = Object.keys(pf);
    if (!pfIds.length) return;
    this._prefill = this.translatePrefill(pfIds, pf);
  }
  
  validatePrefills() {
    try {
      // TODO: would it not be useful to overlay 'fake' data on print? 
      if (this.as.isPrinting) return this._prefill = {};
      if (this.usesServerLinkPrefills()) return this._prefill;
      
      const prefillParam = this.qps.getRawParam('prefill');
      const pf = JSON.parse(prefillParam || "{}");
      const pfIds = Object.keys(pf);
      const valid = pfIds.every(pfId => this.ts.references.some(ref => ref.id === pfId));
      if (valid) {
        return this._prefill = this.translatePrefill(pfIds, pf);
      } else {
        throw new Error("The supplied Prefill value is invalid. Please inform the Form's Builder.");
      }
    } catch (error) {
      this._prefill = {};
      throw {...error, level: 'warning', message: `Prefill Error: ${error.message}`}
    }
  }

  translatePrefill(prefillIds, prefillRecs){
    return prefillIds.reduce((pre, refId) => {
      const rec = prefillRecs[refId];
      const ref = this.ts.references?.find(r => r.id == refId);
      const trans = Object.keys(rec).reduce((newRec, fid) => {
        const str = rec[fid];
        const fieldType = ref?.tableSchema.fields.find(f => f.id == fid)?.fieldType;
        const isNumeric = fieldType !== 'text' && !isNaN(str) && !isNaN(parseFloat(str));
        const val = isNumeric ? parseFloat(str) : str;
        return { ...newRec, [fid]: { value: val, display: val }};
      }, {});
      return { ...pre, [refId]: trans };
    },{});
  }
  
  // to include prefill fields not the form. 
  // Note these have no stdModel to transform them - must have correct URLEncoded value
  nonFormPrefills() {
    return Object.keys(this._prefill).reduce((acc, refId) => {
      const record = this._records[refId];
      if (!record) return acc;
      const fidValues = Object.entries(this._prefill[refId]);
      const nonFormRefEntries = fidValues.reduce((ac2, [fid, pfValue]) => {
        return record[fid] ? ac2 : {...ac2, [fid]: pfValue};
      }, {});
      return Object.keys(nonFormRefEntries).length ? {...acc, [refId]: nonFormRefEntries} : acc;
    }, {});
  }
  
  //#endregion Prefill
  
}
